/**
 * Company Setup Wizard
 * Beautiful 4-step wizard for initial company setup
 * Features: Auto-save, validation, progress indicator, smooth transitions
 */

/* eslint-disable prettier/prettier */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  makeStyles,
  Button,
  Input,
  Field,
  Text,
  Spinner,
  ProgressBar,
} from '@fluentui/react-components';
import {
  BuildingRegular,
  DocumentTextRegular,
  ColorRegular,
  CheckmarkCircleRegular,
  ArrowRightRegular,
  ArrowLeftRegular,
  SaveRegular,
  PhoneRegular,
} from '@fluentui/react-icons';
import { ContentCard } from '../../../components/layout';
import { SearchableDropdown } from '../../../components/common';
import { ApiClient, newToast, normalizeImageUrl } from '../../../configurations/index.config';
import { countries } from '../../../configurations/countries';
import { getErrorMessage } from '../../../types';

const useStyles = makeStyles({
  container: {
    padding: '24px',
    maxWidth: '1200px',
    margin: '0 auto',
  },
  wizardHeader: {
    marginBottom: '32px',
    textAlign: 'left',
  },
  mainTitle: {
    fontSize: '32px',
    fontWeight: '700',
    color: '#008272',
    marginBottom: '8px',
  },
  subtitle: {
    fontSize: '16px',
    color: '#605e5c',
  },
  progressSection: {
    marginBottom: '32px',
  },
  stepsContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '16px',
    position: 'relative',
  },
  stepItem: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    flex: '1',
    position: 'relative',
    zIndex: 1,
  },
  stepCircle: {
    width: '48px',
    height: '48px',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '20px',
    fontWeight: '600',
    marginBottom: '8px',
    transition: 'all 0.3s ease',
    border: '3px solid #e1dfdd',
    backgroundColor: '#ffffff',
    color: '#605e5c',
  },
  stepCircleActive: {
    backgroundColor: '#008272',
    color: '#ffffff',
    border: '3px solid #008272',
    boxShadow: '0 4px 12px rgba(0, 130, 114, 0.3)',
    transform: 'scale(1.1)',
  },
  stepCircleCompleted: {
    backgroundColor: '#008272',
    color: '#ffffff',
    border: '3px solid #008272',
    '& svg': {
      color: '#ffffff',
    },
  },
  stepLabel: {
    fontSize: '13px',
    fontWeight: '500',
    color: '#605e5c',
    textAlign: 'center',
  },
  stepLabelActive: {
    color: '#008272',
    fontWeight: '600',
  },
  stepLine: {
    position: 'absolute',
    top: '24px',
    left: '0',
    right: '0',
    height: '3px',
    backgroundColor: '#e1dfdd',
    zIndex: 0,
  },
  stepLineProgress: {
    height: '100%',
    backgroundColor: '#008272',
    transition: 'width 0.3s ease',
  },
  formCard: {
    marginBottom: '24px',
  },
  formSection: {
    marginBottom: '32px',
  },
  sectionHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    marginBottom: '20px',
    paddingBottom: '12px',
    borderBottom: '2px solid #e1dfdd',
  },
  sectionIcon: {
    fontSize: '24px',
    color: '#008272',
  },
  sectionTitle: {
    fontSize: '18px',
    fontWeight: '600',
    color: '#323130',
  },
  fieldGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  fieldRow: {
    display: 'flex',
    gap: '16px',
    width: '100%',
  },
  fieldFull: {
    flex: '1 1 100%',
  },
  fieldHalf: {
    flex: '1 1 50%',
  },
  fieldThird: {
    flex: '1 1 33%',
  },
  arrayFieldContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  arrayField: {
    display: 'flex',
    gap: '8px',
    alignItems: 'flex-end',
  },
  removeButton: {
    minWidth: 'auto',
    height: '32px',
    backgroundColor: '#d13438',
    color: '#ffffff',
    ':hover': {
      backgroundColor: '#a4262c',
    },
  },
  addButton: {
    marginTop: '8px',
    backgroundColor: 'transparent',
    color: '#008272',
    border: '1px dashed #008272',
    ':hover': {
      backgroundColor: 'rgba(0, 130, 114, 0.05)',
    },
  },
  colorPickerRow: {
    display: 'flex',
    gap: '16px',
    alignItems: 'flex-start',
  },
  colorInputWrapper: {
    display: 'flex',
    flexDirection: 'column',
    flex: '1',
  },
  colorPreview: {
    width: '80px',
    height: '80px',
    borderRadius: '8px',
    border: '2px solid #e1dfdd',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '12px',
    fontWeight: '600',
    color: '#ffffff',
    textShadow: '0 1px 2px rgba(0,0,0,0.3)',
  },
  logoUploadSection: {
    border: '2px dashed #008272',
    borderRadius: '8px',
    padding: '24px',
    textAlign: 'center',
    backgroundColor: 'rgba(0, 130, 114, 0.02)',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    ':hover': {
      backgroundColor: 'rgba(0, 130, 114, 0.05)',
      borderTopColor: '#00635a',
      borderRightColor: '#00635a',
      borderBottomColor: '#00635a',
      borderLeftColor: '#00635a',
    },
  },
  logoPreview: {
    maxWidth: '200px',
    maxHeight: '200px',
    margin: '16px auto',
    borderRadius: '8px',
    border: '1px solid #e1dfdd',
  },
  actionButtons: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: '12px',
    paddingTop: '24px',
    borderTop: '2px solid #e1dfdd',
  },
  buttonGroup: {
    display: 'flex',
    gap: '12px',
  },
  primaryButton: {
    backgroundColor: '#008272',
    color: '#ffffff',
    fontWeight: '600',
    ':hover': {
      backgroundColor: '#00635a',
    },
  },
  secondaryButton: {
    backgroundColor: 'transparent',
    color: '#008272',
    border: '1px solid #008272',
    fontWeight: '600',
    ':hover': {
      backgroundColor: 'rgba(0, 130, 114, 0.05)',
    },
  },
  helperText: {
    fontSize: '12px',
    color: '#605e5c',
    marginTop: '4px',
  },
  errorText: {
    fontSize: '12px',
    color: '#d13438',
    marginTop: '4px',
  },
  requiredIndicator: {
    color: '#d13438',
    marginLeft: '4px',
  },
  savingIndicator: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '13px',
    color: '#605e5c',
  },
  successMessage: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '16px',
    padding: '48px',
    textAlign: 'center',
    flexDirection: 'column',
  },
  successIcon: {
    fontSize: '80px',
    color: '#107c10',
  },
  successTitle: {
    fontSize: '28px',
    fontWeight: '700',
    color: '#323130',
  },
  successSubtitle: {
    fontSize: '16px',
    color: '#605e5c',
    marginBottom: '24px',
  },
});

interface WizardFormData {
  legal_name: string;
  tin: string;
  tax_registration_number: string;
  registered_address: string;
  country: string;
  primary_phone: string;
  secondary_phones: string[];
  primary_email: string;
  secondary_emails: string[];
  website: string;
  logo_url: string;
  logo_thumbnail_url: string;
  tagline: string;
  primary_color: string;
  secondary_color: string;
  accent_color: string;
  invoice_prefix: string;
  memo_prefix: string;
  incident_prefix: string;
  contract_prefix: string;
}

interface ValidationErrors {
  [key: string]: string;
}

const STEPS = [
  { number: 1, label: 'Legal Info', icon: <BuildingRegular /> },
  { number: 2, label: 'Contact Details', icon: <PhoneRegular /> },
  { number: 3, label: 'Branding', icon: <ColorRegular /> },
  { number: 4, label: 'Prefixes', icon: <DocumentTextRegular /> },
];

export const CompanySetupWizard: React.FC = () => {
  const styles = useStyles();
  const navigate = useNavigate();
  
  // Check if we're in edit mode
  const searchParams = new URLSearchParams(window.location.search);
  const isEditMode = searchParams.get('mode') === 'edit';

  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [completed, setCompleted] = useState(false);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string>('');
  const [existingCompanyId, setExistingCompanyId] = useState<number | null>(null);
  const [isDragging, setIsDragging] = useState(false);

  const [formData, setFormData] = useState<WizardFormData>({
    legal_name: '',
    tin: '',
    tax_registration_number: '',
    registered_address: '',
    country: '',
    primary_phone: '',
    secondary_phones: [],
    primary_email: '',
    secondary_emails: [],
    website: '',
    logo_url: '',
    logo_thumbnail_url: '',
    tagline: '',
    primary_color: '#008272',
    secondary_color: '#00635a',
    accent_color: '#107c10',
    invoice_prefix: 'INV-',
    memo_prefix: 'MEM-',
    incident_prefix: 'INC-',
    contract_prefix: 'CON-',
  });

  const [errors, setErrors] = useState<ValidationErrors>({});

  // Auto-save draft every 30 seconds
  useEffect(() => {
    const autoSaveInterval = setInterval(() => {
      if (currentStep > 0 && !completed) {
        saveDraft(false);
      }
    }, 30000);

    return () => clearInterval(autoSaveInterval);
  }, [formData, currentStep, completed]);

  // Load existing company data or draft on mount
  useEffect(() => {
    if (isEditMode) {
      loadExistingCompanyData();
    } else {
      loadDraft();
    }
  }, []);

  const loadExistingCompanyData = async () => {
    setLoading(true);
    try {
      const response = await ApiClient.get('/api/v1/company-setup');
      if (response.data.company) {
        const company = response.data.company;
        setExistingCompanyId(company.company_id);
        setFormData({
          legal_name: company.legal_name || '',
          tin: company.tin || '',
          tax_registration_number: company.tax_registration_number || '',
          registered_address: company.registered_address || '',
          country: company.country || '',
          primary_phone: company.primary_phone || '',
          secondary_phones: company.secondary_phones || [],
          primary_email: company.primary_email || '',
          secondary_emails: company.secondary_emails || [],
          website: company.website || '',
          logo_url: company.logo_url || '',
          logo_thumbnail_url: company.logo_thumbnail_url || '',
          tagline: company.tagline || '',
          primary_color: company.primary_color || '#008272',
          secondary_color: company.secondary_color || '#00635a',
          accent_color: company.accent_color || '#107c10',
          invoice_prefix: company.invoice_prefix || 'INV-',
          memo_prefix: company.memo_prefix || 'MEM-',
          incident_prefix: company.incident_prefix || 'INC-',
          contract_prefix: company.contract_prefix || 'CON-',
        });
        if (company.logo_url) {
          setLogoPreview(normalizeImageUrl(company.logo_url) || '');
        }
      }
    } catch (error) {
      console.error('Failed to load company data:', error);
      newToast('error', 'Failed to load company data');
    } finally {
      setLoading(false);
    }
  };

  const loadDraft = async () => {
    try {
      const response = await ApiClient.get('/api/v1/company-setup/init');
      if (response.data.draft) {
        setFormData({ ...formData, ...response.data.draft.data });
        setCurrentStep(response.data.draft.step || 1);
      }
    } catch (error) {
      console.error('Failed to load draft:', error);
    }
  };

  const saveDraft = async (showToast = true) => {
    setSaving(true);
    try {
      await ApiClient.post('/api/v1/company-setup/draft', {
        step: currentStep,
        data: formData,
      });
      if (showToast) {
        newToast('success', 'Draft saved successfully');
      }
    } catch (error) {
      console.error('Failed to save draft:', error);
    } finally {
      setSaving(false);
    }
  };

  const updateField = (field: keyof WizardFormData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const addArrayItem = (field: 'secondary_phones' | 'secondary_emails') => {
    setFormData(prev => ({
      ...prev,
      [field]: [...prev[field], ''],
    }));
  };

  const updateArrayItem = (
    field: 'secondary_phones' | 'secondary_emails',
    index: number,
    value: string
  ) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].map((item, i) => (i === index ? value : item)),
    }));
  };

  const removeArrayItem = (field: 'secondary_phones' | 'secondary_emails', index: number) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].filter((_, i) => i !== index),
    }));
  };

  const processLogoFile = (file: File) => {
    if (file.size > 2 * 1024 * 1024) {
      newToast('error', 'Logo file must be less than 2MB');
      return;
    }

    const validTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/svg+xml'];
    if (!validTypes.includes(file.type)) {
      newToast('error', 'Logo must be PNG, JPG, or SVG format');
      return;
    }

    setLogoFile(file);
    const reader = new FileReader();
    reader.onload = e => {
      setLogoPreview(e.target?.result as string);
    };
    reader.readAsDataURL(file);
  };

  const handleLogoChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      processLogoFile(file);
    }
  };

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const file = e.dataTransfer.files?.[0];
    if (file) {
      processLogoFile(file);
    }
  };

  const validateStep = (step: number): boolean => {
    const newErrors: ValidationErrors = {};

    if (step === 1) {
      if (!formData.legal_name.trim()) {
        newErrors.legal_name = 'Legal name is required';
      }
      if (!formData.tin.trim()) {
        newErrors.tin = 'TIN is required';
      }
      if (!formData.tax_registration_number.trim()) {
        newErrors.tax_registration_number = 'Tax registration number is required';
      }
      if (!formData.registered_address.trim()) {
        newErrors.registered_address = 'Registered address is required';
      }
      if (!formData.country) {
        newErrors.country = 'Country is required';
      }
    }

    if (step === 2) {
      if (!formData.primary_phone.trim()) {
        newErrors.primary_phone = 'Primary phone is required';
      }
      if (!formData.primary_email.trim()) {
        newErrors.primary_email = 'Primary email is required';
      }

      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (formData.primary_email && !emailRegex.test(formData.primary_email)) {
        newErrors.primary_email = 'Invalid email format';
      }
    }

    if (step === 4) {
      if (!formData.invoice_prefix.trim()) {
        newErrors.invoice_prefix = 'Invoice prefix is required';
      }
      if (!formData.memo_prefix.trim()) {
        newErrors.memo_prefix = 'Memo prefix is required';
      }
      if (!formData.incident_prefix.trim()) {
        newErrors.incident_prefix = 'Incident prefix is required';
      }
      if (!formData.contract_prefix.trim()) {
        newErrors.contract_prefix = 'Contract prefix is required';
      }

      const prefixRegex = /^[A-Z0-9-]+$/;
      ['invoice_prefix', 'memo_prefix', 'incident_prefix', 'contract_prefix'].forEach(field => {
        const value = formData[field as keyof WizardFormData] as string;
        if (value && !prefixRegex.test(value)) {
          newErrors[field] = 'Must contain only uppercase letters, numbers, and hyphens';
        }
        if (value && !value.endsWith('-')) {
          newErrors[field] = 'Must end with a hyphen (-)';
        }
      });
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const nextStep = () => {
    if (validateStep(currentStep)) {
      saveDraft(false);
      setCurrentStep(prev => Math.min(prev + 1, 4));
    }
  };

  const prevStep = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const completeSetup = async () => {
    if (!validateStep(4)) {
      return;
    }

    setLoading(true);
    try {
      if (isEditMode && existingCompanyId) {
        // Edit mode: Use PATCH to update existing company
        await ApiClient.patch('/api/v1/company-setup', { fields: formData });
      } else {
        // Create mode: Use POST to complete initial setup
        await ApiClient.post('/api/v1/company-setup/complete', formData);
      }

      // Then upload logo if provided
      if (logoFile) {
        const logoFormData = new FormData();
        logoFormData.append('logo', logoFile);

        try {
          const logoResponse = await ApiClient.post('/api/v1/company-setup/logo', logoFormData, {
            headers: { 'Content-Type': 'multipart/form-data' },
          });

          console.log('Logo uploaded successfully:', logoResponse.data);
        } catch (logoError: any) {
          console.error('Logo upload failed:', logoError);
          console.error('Logo upload error details:', {
            status: logoError.response?.status,
            statusText: logoError.response?.statusText,
            url: logoError.config?.url,
            data: logoError.response?.data,
          });
          // Don't throw error for logo upload failure after setup is complete
          // Just log it and show a warning
          const errorMsg = logoError.response?.data?.error?.message || logoError.message || 'Unknown error';
          newToast('warning', `${isEditMode ? 'Update' : 'Setup'} completed but logo upload failed: ${errorMsg}`);
        }
      }

      setCompleted(true);
      newToast('success', isEditMode ? 'Company information updated successfully!' : 'Company setup completed successfully!');

      setTimeout(() => {
        navigate('/account/company-setup');
      }, 2000);
    } catch (error: any) {
      console.error('Company setup submission error:', error);
      console.error('Error details:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        url: error.config?.url,
        headers: error.config?.headers,
        data: error.response?.data,
        message: error.message,
      });

      // Extract the actual error message from the backend response
      const errorMessage =
        error.response?.data?.error?.message ||
        error.message ||
        `Failed to complete setup (Status: ${error.response?.status || 'Unknown'})`;
      
      newToast('error', errorMessage);
    } finally {
      setLoading(false);
    }
  };

  if (completed) {
    return (
      <div className={styles.container}>
        <ContentCard>
          <div className={styles.successMessage}>
            <CheckmarkCircleRegular className={styles.successIcon} />
            <Text className={styles.successTitle}>
              {isEditMode ? 'Update Complete!' : 'Setup Complete!'}
            </Text>
            <Text className={styles.successSubtitle}>
              {isEditMode
                ? 'Your company information has been successfully updated.'
                : 'Your company has been successfully configured.'}
              <br />
              Redirecting to company setup page...
            </Text>
            <Spinner size="large" />
          </div>
        </ContentCard>
      </div>
    );
  }

  const progressPercentage = ((currentStep - 1) / (STEPS.length - 1)) * 100;

  return (
    <div className={styles.container}>
      <div className={styles.wizardHeader}>
        <Text className={styles.mainTitle}>
          {isEditMode ? 'Edit Company Information' : 'Company Setup Wizard'}
        </Text>
        <Text className={styles.subtitle}>
          {isEditMode
            ? 'Update your company information across all steps'
            : `Let's configure your company information in ${STEPS.length} easy steps`}
        </Text>
      </div>

      <div className={styles.progressSection}>
        <div className={styles.stepsContainer}>
          <div className={styles.stepLine}>
            <div className={styles.stepLineProgress} style={{ width: `${progressPercentage}%` }} />
          </div>
          {STEPS.map(step => (
            <div key={step.number} className={styles.stepItem}>
              <div
                className={`${styles.stepCircle} ${
                  currentStep === step.number
                    ? styles.stepCircleActive
                    : currentStep > step.number
                      ? styles.stepCircleCompleted
                      : ''
                }`}
              >
                {currentStep > step.number ? (
                  <CheckmarkCircleRegular style={{ color: '#ffffff' }} />
                ) : (
                  step.icon
                )}
              </div>
              <Text
                className={`${styles.stepLabel} ${
                  currentStep === step.number ? styles.stepLabelActive : ''
                }`}
              >
                {step.label}
              </Text>
            </div>
          ))}
        </div>
        <ProgressBar value={progressPercentage / 100} />
      </div>

      <ContentCard>
        {/* Step 1: Legal Information */}
        {currentStep === 1 && (
          <div className={styles.formSection}>
            <div className={styles.sectionHeader}>
              <BuildingRegular className={styles.sectionIcon} />
              <Text className={styles.sectionTitle}>Legal Information</Text>
            </div>

            <div className={styles.fieldGroup}>
              <Field
                label={
                  <>
                    Legal Name <span className={styles.requiredIndicator}>*</span>
                  </>
                }
                validationMessage={errors.legal_name}
                validationState={errors.legal_name ? 'error' : 'none'}
              >
                <Input
                  value={formData.legal_name}
                  onChange={e => updateField('legal_name', e.target.value)}
                  placeholder="ABC Security Services Ltd."
                />
              </Field>

              <div className={styles.fieldRow}>
                <div className={styles.fieldHalf}>
                  <Field
                    label={
                      <>
                        Tax Identification Number (TIN){' '}
                        <span className={styles.requiredIndicator}>*</span>
                      </>
                    }
                    validationMessage={errors.tin}
                    validationState={errors.tin ? 'error' : 'none'}
                    hint="Format depends on country (e.g., 12-3456789 for US)"
                  >
                    <Input
                      value={formData.tin}
                      onChange={e => updateField('tin', e.target.value)}
                      placeholder="12-3456789"
                    />
                  </Field>
                </div>

                <div className={styles.fieldHalf}>
                  <Field
                    label={
                      <>
                        Tax Registration Number <span className={styles.requiredIndicator}>*</span>
                      </>
                    }
                    validationMessage={errors.tax_registration_number}
                    validationState={errors.tax_registration_number ? 'error' : 'none'}
                  >
                    <Input
                      value={formData.tax_registration_number}
                      onChange={e => updateField('tax_registration_number', e.target.value)}
                      placeholder="TRN123456"
                    />
                  </Field>
                </div>
              </div>

              <Field
                label={
                  <>
                    Registered Address <span className={styles.requiredIndicator}>*</span>
                  </>
                }
                validationMessage={errors.registered_address}
                validationState={errors.registered_address ? 'error' : 'none'}
              >
                <Input
                  value={formData.registered_address}
                  onChange={e => updateField('registered_address', e.target.value)}
                  placeholder="123 Main Street, City, State, ZIP"
                />
              </Field>

              <Field
                label={
                  <>
                    Country <span className={styles.requiredIndicator}>*</span>
                  </>
                }
                validationMessage={errors.country}
                validationState={errors.country ? 'error' : 'none'}
              >
                <SearchableDropdown
                  options={countries.map(c => ({ id: c.value, label: c.label, value: c.value }))}
                  selectedValue={formData.country}
                  onSelect={value => updateField('country', value)}
                  placeholder="Select country"
                />
              </Field>
            </div>
          </div>
        )}

        {/* Step 2: Contact Information */}
        {currentStep === 2 && (
          <div className={styles.formSection}>
            <div className={styles.sectionHeader}>
              <PhoneRegular className={styles.sectionIcon} />
              <Text className={styles.sectionTitle}>Contact Information</Text>
            </div>

            <div className={styles.fieldGroup}>
              <Field
                label={
                  <>
                    Primary Phone <span className={styles.requiredIndicator}>*</span>
                  </>
                }
                validationMessage={errors.primary_phone}
                validationState={errors.primary_phone ? 'error' : 'none'}
                hint="Include country code (e.g., +1-555-0123)"
              >
                <Input
                  value={formData.primary_phone}
                  onChange={e => updateField('primary_phone', e.target.value)}
                  placeholder="+1-555-0123"
                />
              </Field>

              <Field label="Secondary Phone Numbers" hint="Add additional phone numbers (optional)">
                <div className={styles.arrayFieldContainer}>
                  {formData.secondary_phones.map((phone, index) => (
                    <div key={index} className={styles.arrayField}>
                      <Input
                        value={phone}
                        onChange={e => updateArrayItem('secondary_phones', index, e.target.value)}
                        placeholder="+1-555-0124"
                        style={{ flex: 1 }}
                      />
                      <Button
                        className={styles.removeButton}
                        onClick={() => removeArrayItem('secondary_phones', index)}
                      >
                        Remove
                      </Button>
                    </div>
                  ))}
                  <Button
                    className={styles.addButton}
                    onClick={() => addArrayItem('secondary_phones')}
                  >
                    + Add Phone Number
                  </Button>
                </div>
              </Field>

              <Field
                label={
                  <>
                    Primary Email <span className={styles.requiredIndicator}>*</span>
                  </>
                }
                validationMessage={errors.primary_email}
                validationState={errors.primary_email ? 'error' : 'none'}
              >
                <Input
                  type="email"
                  value={formData.primary_email}
                  onChange={e => updateField('primary_email', e.target.value)}
                  placeholder="info@company.com"
                />
              </Field>

              <Field label="Secondary Email Addresses" hint="Add additional emails (optional)">
                <div className={styles.arrayFieldContainer}>
                  {formData.secondary_emails.map((email, index) => (
                    <div key={index} className={styles.arrayField}>
                      <Input
                        type="email"
                        value={email}
                        onChange={e => updateArrayItem('secondary_emails', index, e.target.value)}
                        placeholder="support@company.com"
                        style={{ flex: 1 }}
                      />
                      <Button
                        className={styles.removeButton}
                        onClick={() => removeArrayItem('secondary_emails', index)}
                      >
                        Remove
                      </Button>
                    </div>
                  ))}
                  <Button
                    className={styles.addButton}
                    onClick={() => addArrayItem('secondary_emails')}
                  >
                    + Add Email Address
                  </Button>
                </div>
              </Field>

              <Field label="Website" hint="Company website URL (optional)">
                <Input
                  value={formData.website}
                  onChange={e => updateField('website', e.target.value)}
                  placeholder="https://www.company.com"
                />
              </Field>
            </div>
          </div>
        )}

        {/* Step 3: Branding */}
        {currentStep === 3 && (
          <div className={styles.formSection}>
            <div className={styles.sectionHeader}>
              <ColorRegular className={styles.sectionIcon} />
              <Text className={styles.sectionTitle}>Branding & Appearance</Text>
            </div>

            <div className={styles.fieldGroup}>
              <Field label="Company Logo" hint="PNG, JPG, or SVG. Max 2MB. Recommended: 512x512px">
                <div
                  className={styles.logoUploadSection}
                  onDragEnter={handleDragEnter}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                  style={{
                    borderColor: isDragging ? '#00635a' : undefined,
                    backgroundColor: isDragging ? 'rgba(0, 130, 114, 0.08)' : undefined,
                  }}
                >
                  <input
                    type="file"
                    accept=".png,.jpg,.jpeg,.svg"
                    onChange={handleLogoChange}
                    style={{ display: 'none' }}
                    id="logo-upload"
                  />
                  <label htmlFor="logo-upload" style={{ cursor: 'pointer' }}>
                    {logoPreview ? (
                      <img src={logoPreview} alt="Logo preview" className={styles.logoPreview} />
                    ) : (
                      <>
                        <BuildingRegular style={{ fontSize: '48px', color: isDragging ? '#00635a' : '#008272' }} />
                        <Text style={{ display: 'block', marginTop: '12px', color: '#605e5c' }}>
                          {isDragging ? 'Drop image here' : 'Click or drag image to upload'}
                        </Text>
                      </>
                    )}
                  </label>
                </div>
              </Field>

              <Field label="Company Tagline" hint="Short tagline or slogan (optional)">
                <Input
                  value={formData.tagline}
                  onChange={e => updateField('tagline', e.target.value)}
                  placeholder="Securing Tomorrow, Today"
                />
              </Field>

              <Text style={{ fontSize: '16px', fontWeight: '600', marginTop: '16px' }}>
                Color Scheme
              </Text>

              <div className={styles.colorPickerRow}>
                <div className={styles.colorInputWrapper}>
                  <Field label="Primary Color">
                    <input
                      type="color"
                      value={formData.primary_color}
                      onChange={e => updateField('primary_color', e.target.value)}
                      style={{
                        height: '40px',
                        width: '100%',
                        border: '1px solid #d1d1d1',
                        borderRadius: '4px',
                        cursor: 'pointer',
                      }}
                    />
                  </Field>
                </div>
                <div
                  className={styles.colorPreview}
                  style={{ backgroundColor: formData.primary_color }}
                >
                  Primary
                </div>
              </div>

              <div className={styles.colorPickerRow}>
                <div className={styles.colorInputWrapper}>
                  <Field label="Secondary Color">
                    <input
                      type="color"
                      value={formData.secondary_color}
                      onChange={e => updateField('secondary_color', e.target.value)}
                      style={{
                        height: '40px',
                        width: '100%',
                        border: '1px solid #d1d1d1',
                        borderRadius: '4px',
                        cursor: 'pointer',
                      }}
                    />
                  </Field>
                </div>
                <div
                  className={styles.colorPreview}
                  style={{ backgroundColor: formData.secondary_color }}
                >
                  Secondary
                </div>
              </div>

              <div className={styles.colorPickerRow}>
                <div className={styles.colorInputWrapper}>
                  <Field label="Accent Color">
                    <input
                      type="color"
                      value={formData.accent_color}
                      onChange={e => updateField('accent_color', e.target.value)}
                      style={{
                        height: '40px',
                        width: '100%',
                        border: '1px solid #d1d1d1',
                        borderRadius: '4px',
                        cursor: 'pointer',
                      }}
                    />
                  </Field>
                </div>
                <div
                  className={styles.colorPreview}
                  style={{ backgroundColor: formData.accent_color }}
                >
                  Accent
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Step 4: Document Prefixes */}
        {currentStep === 4 && (
          <div className={styles.formSection}>
            <div className={styles.sectionHeader}>
              <DocumentTextRegular className={styles.sectionIcon} />
              <Text className={styles.sectionTitle}>Document Prefixes</Text>
            </div>

            <div className={styles.fieldGroup}>
              <Text style={{ fontSize: '14px', color: '#605e5c', marginBottom: '8px' }}>
                Define prefixes for auto-generated document numbers. Must be uppercase and end with
                a hyphen (-).
              </Text>

              <div className={styles.fieldRow}>
                <div className={styles.fieldHalf}>
                  <Field
                    label={
                      <>
                        Invoice Prefix <span className={styles.requiredIndicator}>*</span>
                      </>
                    }
                    validationMessage={errors.invoice_prefix}
                    validationState={errors.invoice_prefix ? 'error' : 'none'}
                    hint="Example: INV-001"
                  >
                    <Input
                      value={formData.invoice_prefix}
                      onChange={e => updateField('invoice_prefix', e.target.value.toUpperCase())}
                      placeholder="INV-"
                    />
                  </Field>
                </div>

                <div className={styles.fieldHalf}>
                  <Field
                    label={
                      <>
                        Memo Prefix <span className={styles.requiredIndicator}>*</span>
                      </>
                    }
                    validationMessage={errors.memo_prefix}
                    validationState={errors.memo_prefix ? 'error' : 'none'}
                    hint="Example: MEM-001"
                  >
                    <Input
                      value={formData.memo_prefix}
                      onChange={e => updateField('memo_prefix', e.target.value.toUpperCase())}
                      placeholder="MEM-"
                    />
                  </Field>
                </div>
              </div>

              <div className={styles.fieldRow}>
                <div className={styles.fieldHalf}>
                  <Field
                    label={
                      <>
                        Incident Prefix <span className={styles.requiredIndicator}>*</span>
                      </>
                    }
                    validationMessage={errors.incident_prefix}
                    validationState={errors.incident_prefix ? 'error' : 'none'}
                    hint="Example: INC-001"
                  >
                    <Input
                      value={formData.incident_prefix}
                      onChange={e => updateField('incident_prefix', e.target.value.toUpperCase())}
                      placeholder="INC-"
                    />
                  </Field>
                </div>

                <div className={styles.fieldHalf}>
                  <Field
                    label={
                      <>
                        Contract Prefix <span className={styles.requiredIndicator}>*</span>
                      </>
                    }
                    validationMessage={errors.contract_prefix}
                    validationState={errors.contract_prefix ? 'error' : 'none'}
                    hint="Example: CON-001"
                  >
                    <Input
                      value={formData.contract_prefix}
                      onChange={e => updateField('contract_prefix', e.target.value.toUpperCase())}
                      placeholder="CON-"
                    />
                  </Field>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className={styles.actionButtons}>
          <div className={styles.buttonGroup}>
            {currentStep > 1 && (
              <Button
                className={styles.secondaryButton}
                icon={<ArrowLeftRegular />}
                onClick={prevStep}
              >
                Previous
              </Button>
            )}
          </div>

          <div className={styles.buttonGroup}>
            {saving && (
              <div className={styles.savingIndicator}>
                <Spinner size="tiny" />
                <Text>Saving draft...</Text>
              </div>
            )}
            <Button
              className={styles.secondaryButton}
              icon={<SaveRegular />}
              onClick={() => saveDraft(true)}
              disabled={saving}
            >
              Save Draft
            </Button>
            {currentStep < 4 ? (
              <Button
                className={styles.primaryButton}
                icon={<ArrowRightRegular />}
                iconPosition="after"
                onClick={nextStep}
              >
                Next Step
              </Button>
            ) : (
              <Button
                className={styles.primaryButton}
                icon={<CheckmarkCircleRegular />}
                onClick={completeSetup}
                disabled={loading}
              >
                {loading ? <Spinner size="tiny" /> : 'Complete Setup'}
              </Button>
            )}
          </div>
        </div>
      </ContentCard>
    </div>
  );
};

export default CompanySetupWizard;
